import { useState } from "react";
import { Crown, Skull, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CLASS_META } from "@/lib/game";
import PaypalSubscribeButton from "./PaypalSubscribeButton";

export default function RosterDowngradeModal({ data, onResolved }) {
  const { refreshUser } = useAuth();
  const cap = data.cap;
  const [keep, setKeep] = useState([]);
  const [busy, setBusy] = useState(false);

  const toggle = (id) =>
    setKeep((k) => (k.includes(id) ? k.filter((x) => x !== id) : k.length >= cap ? k : [...k, id]));

  const confirm = async () => {
    if (keep.length === 0) return toast.error(`Select up to ${cap} gladiators to keep`);
    setBusy(true);
    try {
      const r = await api.post("/vip/resolve-roster", { keep });
      toast.success(`Kept ${r.data.kept_count}, retired ${r.data.retired_count}.`);
      onResolved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const onRenew = async (res) => {
    await refreshUser();
    if (res?.vip) { toast.success("VIP renewed — all fighters kept!"); onResolved(); }
    else toast.success("Subscription received — once VIP activates your roster is safe.");
  };

  return (
    <div data-testid="downgrade-modal" className="fixed inset-0 z-50 bg-black/85 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-zinc-950 border border-amber-700/50 max-w-3xl w-full p-6 my-8">
        <div className="flex items-center gap-2 mb-2">
          <Crown size={20} className="text-amber-400" />
          <h2 className="font-cinzel text-amber-300 uppercase tracking-widest text-lg">VIP Membership Ended</h2>
        </div>
        <p className="text-sm text-zinc-300 mb-1">
          You hold <span className="text-amber-300 font-semibold">{data.active_count}</span> gladiators, but the free roster cap is <span className="text-amber-300 font-semibold">{cap}</span>.
        </p>
        <p className="text-sm text-zinc-400 mb-4">
          Choose up to {cap} to keep — the rest will be retired. Or renew VIP to keep them all.
        </p>

        <div className="bg-amber-950/20 border border-amber-900/40 p-4 mb-5">
          <p className="text-[11px] uppercase tracking-widest text-amber-300 mb-2">Renew VIP & keep all fighters</p>
          <PaypalSubscribeButton onSuccess={onRenew} />
        </div>

        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] uppercase tracking-widest text-zinc-500">Or pick {cap} to keep</p>
          <span data-testid="keep-count" className={`text-sm tabular-nums ${keep.length > cap ? "text-red-400" : "text-amber-300"}`}>{keep.length}/{cap} selected</span>
        </div>
        <div className="space-y-2 mb-5 max-h-[40vh] overflow-y-auto pr-1">
          {data.gladiators.map((g) => {
            const sel = keep.includes(g.id);
            const meta = CLASS_META[g.cls] || {};
            return (
              <button key={g.id} data-testid={`keep-toggle-${g.id}`} onClick={() => toggle(g.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 border text-left transition-colors ${
                  sel ? "border-amber-600 bg-amber-900/20" : "border-zinc-800 bg-zinc-900/40 hover:border-amber-900/50"}`}>
                <span className={`w-5 h-5 flex items-center justify-center border shrink-0 ${sel ? "bg-amber-600 border-amber-500 text-zinc-950" : "border-zinc-600"}`}>
                  {sel ? <Check size={13} /> : (!sel ? <Skull size={12} className="text-zinc-600" /> : null)}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="text-zinc-100">{g.name}</span>
                  <span className={`ml-2 text-xs ${meta.color}`}>{g.cls}</span>
                </span>
                <span className="text-amber-300 font-semibold tabular-nums shrink-0">{g.rating}</span>
              </button>
            );
          })}
        </div>

        <button data-testid="confirm-downgrade-btn" disabled={busy || keep.length === 0}
          onClick={confirm}
          className="w-full py-3 font-cinzel uppercase tracking-widest bg-red-900/40 border border-red-700/60 text-red-200 hover:bg-red-900/60 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
          Keep {keep.length} & Retire the Rest
        </button>
      </div>
    </div>
  );
}
